// NAV_ITEMS — typisierte Navigationsstruktur.
//
// Story 0.2 (v2-Relaunch, Welle 2): Die Struktur folgt jetzt dem finalen
// Design-Prototypen (claude-design-import/assets/layout.js + sitemap.html),
// nicht mehr dem Paritaets-Altstand. Aenderungen gegenueber v1:
//
//   * Sektion "Hotspots & Wallets" -> "Hotspots" (Wallets liegen in Übersicht)
//   * Sektion "Statistiken" ist in "Einblicke" aufgegangen
//   * Sektion "Entwickler" ist in "Premium" aufgegangen (ein Developer-Portal
//     statt drei Einzeleinträge — API-Tokens/Webhooks/API-Docs sind über
//     /developer erreichbar)
//   * Sektion "Info" ist in "Konto" aufgegangen
//   * Standortgeber / Provisions-Regeln / Abrechnungen liegen unter
//     /hosts, /splits, /settlements (Host-Modell, Epic 2 — NEUE Entitaet,
//     ersetzt NICHT das bestehende Contact-Modell, siehe api-types-Kommentar
//     auf Host). Die alten Pfade /commissions, /payouts bleiben als
//     Redirects bestehen — siehe LEGACY_PATH_REDIRECTS.
//   * Fix (2026-09-01): /contacts wurde fälschlich auf /hosts umgeleitet,
//     als sei Contact durch Host ersetzt worden — war es nicht. Contact
//     bleibt eine eigene, weiterhin genutzte Entitaet mit eigenem Nav-Eintrag
//     "Kontakte".
//   * Neu: Earn-Rate ($/GB-Netzmonitor) in "Einblicke"
//   * Neu: Speedtest (Tag "Mobile") in "Hotspots"
//
// Labels hier sind DE-Fallbacks; die tatsaechliche Anzeige kommt aus i18n
// (shell.json -> nav.items.<id>).

export type NavItem = {
  id: string
  label: string
  href: string
  icon: string
  badge?: string
  tag?: string
  /** If true, only show for admin users or users with site_id */
  adminOnly?: boolean
}

export type NavSection = {
  section: string
}

export type NavEntry = NavSection | NavItem

export function isSection(entry: NavEntry): entry is NavSection {
  return 'section' in entry
}

export function isNavItem(entry: NavEntry): entry is NavItem {
  return 'id' in entry
}

export const NAV_ITEMS: NavEntry[] = [
  { section: 'Übersicht' },
  { id: 'dashboard', label: 'Dashboard', href: '/dashboard', icon: 'LayoutGrid' },
  { id: 'activities', label: 'Aktivitäten', href: '/activities', icon: 'Activity' },
  { id: 'wallets', label: 'Meine Wallets', href: '/wallets', icon: 'Wallet' },
  { id: 'watched-wallets', label: 'Beobachtete Wallets', href: '/wallets/watched', icon: 'Eye' },
  { id: 'notes', label: 'Notizen', href: '/notes', icon: 'StickyNote' },

  { section: 'Hotspots' },
  { id: 'hotspots', label: 'Meine Hotspots', href: '/hotspots', icon: 'Radio' },
  { id: 'hotspots-map', label: 'Karte', href: '/hotspots/map', icon: 'Map' },
  { id: 'tags', label: 'Tags', href: '/tags', icon: 'Tag' },
  // Welle-2-Review F7: Der Design-Prototyp (layout.js, Sektion "Hotspots")
  // fuehrt Speedtest mit dem Tag "Mobile"; der Eintrag fehlte bisher. Ziel ist
  // die i18n-Platzhalterroute /speedtest (Muster /earn-rate) — Story 5.5 fuellt
  // sie mit der echten Messansicht.
  { id: 'speedtest', label: 'Speedtest', href: '/speedtest', icon: 'Zap', tag: 'Mobile' },

  { section: 'Einnahmen' },
  // v2-final: Contact + Host sind zu EINER Entitaet "People" verschmolzen
  // (SPEC.md Global decisions, 2026-09-01). /contacts und /hosts bleiben als
  // Redirects erreichbar, haben aber keinen eigenen Nav-Eintrag mehr.
  { id: 'people', label: 'Personen', href: '/people', icon: 'Users' },
  { id: 'splits', label: 'Provisions-Regeln', href: '/splits', icon: 'Handshake' },
  { id: 'settlements', label: 'Abrechnungen', href: '/settlements', icon: 'PieChart' },

  { section: 'Einblicke' },
  { id: 'earn-rate', label: 'Earn-Rate', href: '/earn-rate', icon: 'Gauge', tag: '$/GB' },
  // #0409: Top-Städte aus der Navigation entfernt — die Route ist deaktiviert
  // (siehe src/routes/_auth.top-cities.disabled.tsx), die Zahlen sind
  // eingefroren/teils falsch. Reaktivierung: Zeile wieder einkommentieren,
  // sobald die Route reaktiviert ist.
  // { id: 'top-cities', label: 'Top Städte', href: '/top-cities', icon: 'MapPin' },
  { id: 'top-spots', label: 'Top Spots', href: '/top-spots', icon: 'TrendingUp' },
  { id: 'news', label: 'News & Updates', href: '/news', icon: 'Newspaper' },
  { id: 'notif-log', label: 'Benachrichtigungen', href: '/notifications', icon: 'Bell' },

  { section: 'Premium' },
  { id: 'plans', label: 'Pläne', href: '/plans', icon: 'Crown' },
  { id: 'subscription', label: 'Abo & Zahlung', href: '/account/subscription', icon: 'Gem' },
  { id: 'developer', label: 'Developer', href: '/developer', icon: 'Code' },
  { id: 'monthly-report', label: 'Monatsbericht', href: '/monthly-report', icon: 'BarChart2' },
  { id: 'payment-history', label: 'Zahlungshistorie', href: '/payments', icon: 'CreditCard' },

  { section: 'Konto' },
  { id: 'settings', label: 'Einstellungen', href: '/settings', icon: 'Settings' },
  // v2-final: Sektion "Konto" endet nach Support (layout.js). Hilfe/Über uns/
  // Rechtliches bleiben als Routen erreichbar (AppFooter verlinkt sie), sind
  // aber nicht mehr in der Seitenleiste.
  { id: 'support', label: 'Support', href: '/support', icon: 'MessageCircle' },

  { section: 'Admin' },
  { id: 'admin-revenue', label: 'Umsatz', href: '/admin/revenue', icon: 'DollarSign', adminOnly: true },
  { id: 'admin-users', label: 'Userverwaltung', href: '/admin/users', icon: 'ShieldCheck', adminOnly: true },
  { id: 'admin-support-tickets', label: 'Support-Tickets', href: '/admin/support-tickets', icon: 'LifeBuoy', adminOnly: true },
  // Die Newsletter-Seite existierte schon, war aber nur per URL erreichbar.
  { id: 'admin-newsletters', label: 'Newsletter', href: '/admin/newsletters', icon: 'Newspaper', adminOnly: true },
]

/**
 * Navigations-Mapping v1 -> v2 (Story 0.2).
 *
 * Single Source of Truth für die Legacy-Redirects. Jeder alte Pfad bleibt
 * erreichbar und leitet dauerhaft (replace: true) auf den neuen kanonischen
 * Pfad um — Bookmarks, E-Mail-Links und alte `Link to=`-Aufrufe im Code
 * funktionieren dadurch unveraendert weiter.
 *
 * Die Route-Dateien unter src/routes referenzieren diese Tabelle nicht zur
 * Laufzeit (TanStack-Routen brauchen String-Literale für die Typinferenz),
 * sondern sie ist die dokumentierte Referenz + Testgrundlage.
 */
export const LEGACY_PATH_REDIRECTS: Readonly<Record<string, string>> = {
  '/commissions': '/splits',
  '/commissions/create': '/splits/create',
  '/commissions/$id': '/splits/$id',
  '/payouts': '/settlements',
  '/premium': '/plans',
  // Welle-2-Review F8: produktive Mail-/Backend-Links zeigen auf Unterpfade
  // der alten Tarifseite (/premium/trial, /premium/select_upgrade). Der Splat
  // faengt sie alle ein — sonst 404.
  '/premium/$': '/plans',
  // v2-final: People-Merge (SPEC.md Global decisions)
  '/contacts': '/people',
  '/contacts/$id': '/people/$id',
  '/hosts': '/people',
  '/hosts/$id': '/people/$id',
  // Issue #36: Sites (Whitelist-Feature) wird vorerst nicht mehr angeboten
  // (Dennis-Entscheid). Nav-Eintrag entfernt, Routen bleiben als Redirect
  // aufs Dashboard erreichbar. Code/Tests unter features/sites bleiben
  // bestehen, nur der Zugang ist gekappt.
  '/sites': '/dashboard',
  '/sites/$id': '/dashboard',
}

/**
 * Befüllt den Badge des "Meine Hotspots"-Eintrags dynamisch mit der echten
 * Hotspot-Anzahl (F-094). Vorher war der Wert "12" hartkodiert.
 *
 * - count undefined (lädt noch) → keine Badge
 * - count 0 → keine Badge (kein leeres "0"-Bubble)
 * - count > 0 → Badge mit der Zahl
 *
 * Reine Funktion: das übergebene NAV_ITEMS-Array wird nicht mutiert.
 */
export function withHotspotBadge(items: NavEntry[], count: number | undefined): NavEntry[] {
  return items.map((entry) => {
    if (isNavItem(entry) && entry.id === 'hotspots') {
      if (count == null || count <= 0) {
        // Keine Badge anzeigen (lädt noch oder 0 Hotspots).
        const { badge: _badge, ...rest } = entry
        return rest
      }
      return { ...entry, badge: String(count) }
    }
    return entry
  })
}

/**
 * Nav-Eintraege, die hinter Premium liegen (F3, siehe premium-gates-Auftrag).
 * Review-Fix: developer/monthly-report/payment-history stehen bereits in der
 * Sektion "Premium" (siehe NAV_ITEMS oben) — ein zusaetzliches Tag waere
 * redundant. Nur splits/settlements liegen ausserhalb dieser Sektion.
 */
const PREMIUM_TAGGED_IDS = new Set(['splits', 'settlements'])

/**
 * Markiert die Premium-gated Nav-Eintraege mit `tag: 'Premium'`, solange der
 * Account kein Premium hat. Bei is_premium === true (oder noch unbekannt,
 * `isPremium` undefined) bleiben die Eintraege unveraendert.
 *
 * Reine Funktion: das uebergebene NAV_ITEMS-Array wird nicht mutiert.
 */
export function withPremiumTag(items: NavEntry[], isPremium: boolean | undefined): NavEntry[] {
  if (isPremium !== false) return items
  return items.map((entry) => {
    if (isNavItem(entry) && PREMIUM_TAGGED_IDS.has(entry.id)) {
      return { ...entry, tag: 'Premium' }
    }
    return entry
  })
}
